import React, { useEffect, useRef, useState, FC, memo } from 'react';
import { observer } from 'mobx-react-lite';
import { Form, Input, Button, notification } from '@ss/mtd-react';
import { Select } from '@block/plug';
import { useRequest } from 'ahooks';
import { useRootStore } from '@src/model';
import { getVerificationCode, changPhoneBind, bindPhone } from '@src/service';
import { loginCodeSend } from '@service/login';
import YodaSlide, { IYodaSlideRef } from '@elements/YodaSlide';
import { isPCCheck } from '@utils/tools';
import './index.scss';

const FormItem = Form.Item;
const regs = {
  forPhone: /^[0-9]*$/,
  forVerifiCode: /^[0-9]{6}$/,
};

export enum BindPhoneTypeEnum {
  exchange = 'exchange',
  firstBind = 'firstBind',
}

/**
 * 更换手机/绑定手机 如果是绑定手机号 调用/api/official/user/phone/bind
 */
interface Iprops {
  type: BindPhoneTypeEnum; // 换绑 或 微信扫码初次绑定
  onClose: () => void;
  onConfirm: () => void;
}
const BindOrExchangePhone: FC<Iprops> = observer(({ type, onClose, onConfirm }) => {
  const {
    user: { fetchAreaCodeList },
    loginStore: { userInfo },
  } = useRootStore();

  const formRef = useRef(null);
  const yodaSlideRef = useRef<IYodaSlideRef | null>(null); // yoda滑块ref
  const countdownRef = useRef<ReturnType<typeof setTimeout> | null>(null); // 倒计时ref
  const [countdown, setCountdown] = useState(0);
  const [areaCode, setAreaCode] = useState('86');
  const [yodaCallbackRequestCode, setYodaCallbackRequestCode] = useState(''); // 滑块的request_code,用于监听变化，以更新倒计时
  const [isSuccessOfYodaValidate, setSsSuccessOfYodaValidate] = useState(false); // Yoda滑块验证是否成功
  const { runAsync: runRequestCode } = useRequest(getVerificationCode, { throttleWait: 1000, manual: true }); // 获取滑块的request_code
  const { runAsync: runSendVerifyCode } = useRequest(loginCodeSend, { manual: true }); // 获取短信验证接口
  const { runAsync: runChangePhoneBind } = useRequest(changPhoneBind, { throttleWait: 1000, manual: true }); // 解绑手机号
  const { runAsync: runBindPhone } = useRequest(bindPhone, { manual: true }); // 绑定手机号
  const isExchangeType = type === BindPhoneTypeEnum.exchange;

  // 发送短信验证码
  const sendVerifyCode = async () => {
    // @ts-ignore
    const getFormVal = formRef.current.getFieldsValue();
    const params = {
      countryCode: getFormVal?.areaInfo?.id,
      areaName: getFormVal?.areaInfo?.name,
      phone: getFormVal?.phone,
      platform: isPCCheck() ? 1 : 3,
      yodaAction: isExchangeType ? 'recruitmentaccountrebind' : 'recruitmentaccountbind',
    };
    await runSendVerifyCode(params);
    notification.success({
      message: '短信验证码已发送成功',
    });
    setCountdown(60);
  };

  // 通过监听isSuccessOfYodaValidate 和 yodaCallbackRequestCode的方式发送验证码
  useEffect(() => {
    if (!isSuccessOfYodaValidate) return;
    sendVerifyCode();
  }, [isSuccessOfYodaValidate, yodaCallbackRequestCode]);

  // 验证码倒计时
  useEffect(() => {
    if (countdown < 0) return;
    countdownRef.current = setTimeout(() => {
      setCountdown(countdown - 1);
      if (countdown <= 0 && countdownRef.current) {
        clearTimeout(countdownRef.current);
      }
    }, 1000);
  }, [countdown]);

  // 获取滑块验证码
  const validateBeforeSend = async () => {
    if (!yodaSlideRef?.current?.isSuccessLoadYodaFn()) return;
    // @ts-ignore
    const isValid = formRef.current.validateFields(['areaInfo', 'phone']);
    // @ts-ignore
    const getFormVal = formRef.current.getFieldsValue();
    if (!isValid) return;
    // 换绑 手机号和原来的一样 提示并拦截
    if (isExchangeType && getFormVal?.areaInfo?.id === userInfo?.countryCode && getFormVal?.phone === userInfo?.phone) {
      notification.warning({
        message: '无须换绑',
      });
      return;
    }

    try {
      const params = {
        countryCode: getFormVal?.areaInfo?.id,
        phone: getFormVal?.phone,
        platform: isPCCheck() ? 1 : 3,
        yodaAction: isExchangeType ? 'recruitmentaccountrebind' : 'recruitmentaccountbind',
      };
      const res = await runRequestCode(params);
      yodaSlideRef.current.initYoda(res?.requestCode);
    } catch (e) {
      console.error(e);
    }
  };

  // 换绑手机号
  const onConfirmExhange = async () => {
    // @ts-ignore
    const isValid = formRef.current.validateFields();
    if (!isValid) return;
    // @ts-ignore
    const getFormVal = formRef.current.getFieldsValue();
    // 换绑 手机号和原来的一样 提示并拦截
    if (isExchangeType && getFormVal?.areaInfo?.id === userInfo?.countryCode && getFormVal?.phone === userInfo?.phone) {
      notification.warning({
        message: '无须换绑',
      });
      return;
    }
    const params = {
      areaName: getFormVal?.areaInfo?.name,
      countryCode: getFormVal?.areaInfo?.id,
      phone: getFormVal?.phone,
      code: getFormVal?.code.replace(/\D/g, ''),
      platform: isPCCheck() ? 1 : 3,
      yodaAction: isExchangeType ? 'recruitmentaccountrebind' : 'recruitmentaccountbind',
    };
    if (type === BindPhoneTypeEnum.exchange) {
      // 换绑添加原手机号，判断当前登录用户，是否该手机号
      const params2 = Object.assign(params, { originalAccountId: userInfo?.accountId });
      // 换绑
      await runChangePhoneBind(params2);
    } else {
      // 绑定
      await runBindPhone(params);
    }
    // 1-如果手机号没有被注册，且在ATS中没有关联简历 更换手机号成功 跳转到登录页面
    notification.success({
      message: `${type === BindPhoneTypeEnum.exchange ? '更换' : '绑定'}手机号成功`,
    });
    // 2-如果手机号已被注册，且ATS有简历，则提示 “手机号已经被注册，请直接登录”
    // 3-如果手机号已被注册，但ATS中没有简历 ，显示“该手机号已被注册，请直接登录”，页面跳转到系统登录页
    // notification.success({
    //   message: '该手机号已被注册，请直接登录',
    // });
    onConfirm();
  };

  const onFieldValueChange = (key, value) => {
    if (key === 'areaInfo') {
      setAreaCode(value?.id);
    }
  };

  return (
    <div className="exchange_phone">
      <div className="label">{isExchangeType ? '请输入新的手机号码' : '请输入绑定的手机号码'}</div>
      <Form
        ref={formRef}
        className="login_form"
        onFieldValueChange={onFieldValueChange}
        defaultFieldsValue={{
          areaInfo: {
            name: '中国大陆',
            id: '86',
          },
        }}
      >
        <FormItem formItemKey="areaInfo" span="6" className="areaInfo" required message="请选择">
          <Select
            toFormItem
            placeholder="请选择"
            clearable={false}
            keyField="id"
            labelField="name"
            renderInputLabel={(option) => `+${option?.id}`}
            popLayer={{
              width: 374,
              maxHeight: 180,
              zIndex: 1066,
            }}
            renderOptionContent={(option) => `${option.name}(${option.id})`}
            onFetchData={async (filter) => {
              const data = await fetchAreaCodeList(filter);
              return data;
            }}
          />
        </FormItem>
        <FormItem
          formItemKey="phone"
          span="18"
          className="phone"
          rules={[
            { required: true, message: '手机号码有误' },
            { whitespace: true, message: '请输入手机号' },
            { pattern: regs.forPhone, message: '请输入正确手机号' },
            {
              validator: (rule, value, callback) => {
                // @ts-ignore
                const interCode = formRef.current.getFieldsValue().areaInfo?.id;
                const reg = /^\d{11}$/;
                if (interCode === '86' && !reg.test(value)) {
                  callback('手机号码格式有误');
                }
                callback();
              },
            },
          ]}
        >
          <Input
            toFormItem
            type="number"
            placeholder={areaCode === '86' ? '请输入11位手机号码' : '请输入手机号码'}
            maxLength="20"
          />
        </FormItem>
        <FormItem
          formItemKey="code"
          className="verify_code"
          rules={[
            { required: true, message: '请输入短信验证码' },
            {
              validator: (rule, value, callback) => {
                const val = value?.replace(/\D/g, '');
                const reg = /^\d{6}$/;
                if (!reg.test(val)) {
                  callback('请输入正确短信验证码');
                }
                callback();
              },
            },
          ]}
        >
          <div className="verify_wrapper">
            <Input toFormItem placeholder="验证码" maxLength={6} />
            <Button
              className="get-code-btn"
              shape="text"
              disabled={countdown > 0}
              onClick={() => {
                validateBeforeSend();
              }}
            >
              {countdown > 0 ? `${countdown}秒后重新获取` : '获取验证码'}
            </Button>
          </div>
        </FormItem>
      </Form>
      <div className="btns">
        <Button size="large" className="cancel" onClick={onClose}>
          取消
        </Button>
        <Button size="large" type="warning" className="confirm" onClick={onConfirmExhange}>
          确定
        </Button>
      </div>
      <YodaSlide
        ref={yodaSlideRef}
        yodaSlideDialogWrapId="zp_yodaLogin_root"
        yodaSlideSuccCallback={(options) => {
          // 滑块验证通过发送短信验证码
          setYodaCallbackRequestCode(options ? options.requestCode : '');
          setSsSuccessOfYodaValidate(true);
        }}
        yodaSlideFailCallback={() => {
          setSsSuccessOfYodaValidate(false);
        }}
      />
    </div>
  );
});

export default memo(BindOrExchangePhone);
